import { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Modal, Button, Container, Row, Col, Card, ListGroup, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import '../styles/styles.css';
import CustomNavbar from './NavBar';

const BandLeader = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [queue, setQueue] = useState<any[]>([]);
  const [showReport, setShowReport] = useState(false);
  const [completedSongs, setCompletedSongs] = useState<any[]>([]);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakDuration, setBreakDuration] = useState<number>(30);
  const [isBreakActive, setIsBreakActive] = useState(false);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchRequests = () => {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setRequests(res.data));
    };
    const fetchQueue = () => {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/queue`)
        .then(res => setQueue(res.data));
    };
    const checkBreakStatus = () => {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/breaks/status`)
        .then(res => setIsBreakActive(res.data.isActive))
        .catch(err => console.error('Failed to check break status:', err));
    };
    fetchRequests();
    fetchQueue();
    checkBreakStatus();
    const interval = setInterval(() => {
      fetchRequests();
      fetchQueue();
      checkBreakStatus();
    }, 5000);
    return () => clearInterval(interval);
  }, [token, navigate]);

  const formatPhoneNumber = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length !== 10) return phone;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  };

  const addToQueue = (id: number) => {
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/${id}/queue`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        // Update the song to queued and set SubmittedAt
        const updatedRequest = requests.find((r: any) => r.id === id);
        if (!updatedRequest) return;
        const newRequest = { ...updatedRequest, isQueued: true, SubmittedAt: new Date().toISOString() };

        // Create new requests array: non-queued first, then queued sorted by SubmittedAt, with new request at the end
        const nonQueued = requests.filter((r: any) => !r.isQueued || r.isCompleted || r.id === id);
        const queued = requests
          .filter((r: any) => r.isQueued && !r.isCompleted && r.id !== id)
          .sort((a: any, b: any) => new Date(a.SubmittedAt).getTime() - new Date(b.SubmittedAt).getTime());

        setRequests([...nonQueued, ...queued, newRequest]);
      });
  };

  const removeFromQueue = (id: number) => {
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/${id}/dequeue`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setRequests(requests.map((r: any) => r.id === id ? { ...r, isQueued: false } : r)));
  };

  const deleteRequest = (id: number) => {
    axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setRequests(requests.filter((r: any) => r.id !== id)));
  };

  const moveToNext = () => {
    const completedAt = new Date().toISOString();
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/next`, { completedAt }, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        const queued = requests.filter((r: any) => r.isQueued && !r.isCompleted);
        if (queued.length > 0) {
          setRequests(requests.map((r: any) => r.id === queued[0].id ? { ...r, isQueued: false, isCompleted: true, completedAt } : r));
        }
      });
  };

  const moveToPrevious = () => {
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/previous`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const previousSong = res.data;
        if (previousSong) {
          setRequests(requests.map((r: any) =>
            r.id === previousSong.id
              ? { ...r, isQueued: true, isCompleted: false, completedAt: null }
              : r
          ));
        }
      })
      .catch(err => {
        console.error('Failed to move to previous song:', err);
        alert('No previous song available or error occurred');
      });
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const queuedRequests = requests.filter((r: any) => r.isQueued && !r.isCompleted);
    const [reorderedItem] = queuedRequests.splice(result.source.index, 1);
    queuedRequests.splice(result.destination.index, 0, reorderedItem);

    const newRequests = [
      ...requests.filter((r: any) => !r.isQueued || r.isCompleted),
      ...queuedRequests
    ];

    setRequests(newRequests);

    const requestIds = queuedRequests.map((r: any) => r.id);
    axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/reorder`, requestIds, { headers: { Authorization: `Bearer ${token}` } })
      .catch(err => console.error('Reorder failed:', err));
  };

  const endShow = () => {
    const confirmEnd = window.confirm("Are you sure you want to end the show? This will clear out the entire queue and all pending requests.");
    if (confirmEnd) {
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/endshow`, {}, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setCompletedSongs(res.data);
          setRequests([]);
          setShowReport(true);
        })
        .catch(err => {
          console.error('Failed to end show:', err);
          alert('Failed to end the show. Please try again.');
        });
    }
  };

  const startBreak = () => {
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/breaks/start`, { durationMinutes: breakDuration }, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setIsBreakActive(true);
        setShowBreakModal(false);
      })
      .catch(err => {
        console.error('Failed to start break:', err);
        alert('Failed to start break.');
      });
  };

  const endBreak = () => {
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/breaks/end`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setIsBreakActive(false))
      .catch(err => {
        console.error('Failed to end break:', err);
        alert('Failed to end break.');
      });
  };

  const exportReport = () => {
    const csvContent = [
      "Name,Song Title,Artist,Completed At",
      ...completedSongs.map((req: any) => `${req.name},${req.song.title},${req.song.artist.name},${new Date(req.completedAt).toLocaleString()}`)
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'karaoke_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openChordChart = (songId: number) => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songs/${songId}/chordchart`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      })
      .catch(err => {
        console.error('Failed to open chord chart:', err.message);
        alert('Failed to open chord chart. It may not exist or there was an error.');
      });
  };

  if (!token) return (
    <Container className="mt-5 text-center">
      <h3 className="text-light animate__animated animate__pulse" style={{ fontFamily: "'Poppins', sans-serif" }}>
        Please log in to continue
      </h3>
    </Container>
  );

  const queuedRequests = requests
    .filter((r: any) => r.isQueued && !r.isCompleted)
    .sort((a: any, b: any) => new Date(a.SubmittedAt).getTime() - new Date(b.SubmittedAt).getTime());
  const pendingRequests = requests.filter((r: any) => !r.isQueued && !r.isCompleted);

  return (
    <>
      <CustomNavbar />
      <Container fluid className="mt-5 mb-5">
        <Row className="mb-4 animate__animated animate__fadeIn">
          <Col>
            <h2 className="text-center text-light" style={{
              fontFamily: "'blowbrush', sans-serif",
              letterSpacing: '1px',
              fontSize: '3.5rem'
            }}>
              BAND LEADER DASHBOARD
            </h2>
          </Col>
        </Row>
        <Row className="mb-4 animate__animated animate__fadeIn" style={{ animationDelay: '0.2s' }}>
          <Col className="text-center">
            <Button className="btn-custom-primary me-2" onClick={moveToPrevious}>Previous Song</Button>
            <Button className="btn-custom-primary me-2" onClick={moveToNext}>Next Song</Button>
            <Button
              className={"btn-custom-primary me-2"}
              onClick={isBreakActive ? endBreak : () => setShowBreakModal(true)}
            >
              {isBreakActive ? "End Break" : "Take a Break"}
            </Button>
            <Button className="btn-custom-danger" onClick={endShow}>End Show</Button>
          </Col>
        </Row>

        <Row className="animate__animated animate__fadeIn" style={{ animationDelay: '0.4s' }}>
          <Col>
            <Card className="card-custom">
              <Card.Header as="h3" style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif" }}>
                Queue
              </Card.Header>
              <Card.Body>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="queue">
                    {(provided) => (
                      <table className="table table-striped table-custom" {...provided.droppableProps} ref={provided.innerRef}>
                        <thead>
                          <tr>
                            <th style={{ width: '30px' }}></th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Song</th>
                            <th>Key</th> {/* New column */}
                            <th>Action</th>
                            <th>Lyrics</th>
                            <th>Chords</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queuedRequests.map((req: any, index: number) => (
                            <Draggable key={req.id} draggableId={req.id.toString()} index={index}>
                              {(provided, snapshot) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`draggable-row animate__animated animate__slideInUp ${snapshot.isDragging ? 'dragging' : ''}`}
                                  style={{ animationDelay: `${0.1 * index}s`, ...provided.draggableProps.style }}
                                >
                                  <td {...provided.dragHandleProps} style={{ cursor: 'grab', width: '30px' }}>
                                    ☰
                                  </td>
                                  <td>{req.name}</td>
                                  <td>{formatPhoneNumber(req.phoneNumber)}</td>
                                  <td>{req.song.artist.name} - {req.song.title}</td>
                                  <td>{req.song.key || '-'}</td> {/* Display key */}
                                  <td>
                                    <Button
                                      className="btn-custom-danger"
                                      size="sm"
                                      onClick={() => removeFromQueue(req.id)}
                                    >
                                      Remove
                                    </Button>
                                  </td>
                                  <td>
                                    {req.song.songLyrics && req.song.songLyrics.length >= 12 ? (
                                      <Button
                                        className="btn-custom-primary"
                                        size="sm"
                                        onClick={() => navigate(`/lyrics/${req.song.id}`)}
                                      >
                                        View Lyrics
                                      </Button>
                                    ) : (
                                      <span className="text-muted">No lyrics</span>
                                    )}
                                  </td>
                                  <td>
                                    {req.song.chordChartPath ? (
                                      <Button
                                        className="btn-custom-primary"
                                        size="sm"
                                        onClick={() => openChordChart(req.song.id)}
                                      >
                                        View Chords
                                      </Button>
                                    ) : (
                                      <span className="text-muted">No chords</span>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      </table>
                    )}
                  </Droppable>
                </DragDropContext>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4 animate__animated animate__fadeIn" style={{ animationDelay: '0.6s' }}>
          <Col>
            <Card className="card-custom">
              <Card.Header as="h3" style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif" }}>
                Pending Requests
              </Card.Header>
              <Card.Body>
                <table className="table table-striped table-custom">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Song</th>
                      <th>Key</th> {/* New column */}
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((req: any, index: number) => (
                      <tr key={req.id} className="animate__animated animate__slideInUp" style={{ animationDelay: `${0.1 * index}s` }}>
                        <td>{req.name}</td>
                        <td>{formatPhoneNumber(req.phoneNumber)}</td>
                        <td>{req.song.artist.name} - {req.song.title}</td>
                        <td>{req.song.key || '-'}</td> {/* Display key */}
                        <td>
                          <Button className="btn-custom-primary me-2" size="sm" onClick={() => addToQueue(req.id)}>Add to Queue</Button>
                          <Button className="btn-custom-danger" size="sm" onClick={() => deleteRequest(req.id)}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4 animate__animated animate__fadeIn" style={{ animationDelay: '0.8s' }}>
          <Col>
            <Card className="card-custom">
              <Card.Header as="h3" style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif" }}>
                Live Queue
              </Card.Header>
              <Card.Body className="text-center">
                {queue.length > 0 ? (
                  <>
                    <h4 className="mb-4" style={{ color: '#d63384', fontFamily: "'Montserrat', sans-serif" }}>
                      Now Singing: {queue[0].name} - {queue[0].song.artist.name} - {queue[0].song.title} ({queue[0].song.key || 'No Key'})
                    </h4>
                    <h5 style={{ fontFamily: "'Montserrat', sans-serif", color: '#495057' }}>Up Next:</h5>
                    <ListGroup variant="flush" className="mx-auto shadow-sm" style={{ maxWidth: '600px', borderRadius: '10px', backgroundColor: 'white' }}>
                      {queue.slice(1, 4).map((req: any, index: number) => (
                        <ListGroup.Item key={req.id} className="py-3 animate__animated animate__slideInUp" style={{ animationDelay: `${0.1 * (index + 1)}s` }}>
                          {req.name} - {req.song.artist.name} - {req.song.title} ({req.song.key || 'No Key'})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                ) : (
                  <p className="text-muted animate__animated animate__pulse" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    No songs in queue yet.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal show={showBreakModal} onHide={() => setShowBreakModal(false)}>
          <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white' }}>
            <Modal.Title style={{ fontFamily: "'Poppins', sans-serif'" }}>Take a Break</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group controlId="breakDuration">
                <Form.Label>Break Duration (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  value={breakDuration}
                  onChange={(e) => setBreakDuration(Number(e.target.value))}
                  min="1"
                  max="60"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button className="btn-custom-secondary" onClick={() => setShowBreakModal(false)}>Cancel</Button>
            <Button className="btn-custom-primary" onClick={startBreak}>Start Break</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showReport} onHide={() => setShowReport(false)} size="lg">
          <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white' }}>
            <Modal.Title style={{ fontFamily: "'Poppins', sans-serif" }}>Show Report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5 style={{ fontFamily: "'Montserrat', sans-serif" }}>Completed Songs</h5>
            {completedSongs.length > 0 ? (
              <table className="table table-bordered table-custom">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Song</th>
                    <th>Completed At</th>
                  </tr>
                </thead>
                <tbody>
                  {completedSongs.map((req: any, index: number) => (
                    <tr key={req.id} className="animate__animated animate__slideInUp" style={{ animationDelay: `${0.1 * index}s` }}>
                      <td>{req.name}</td>
                      <td>{req.song.artist.name} - {req.song.title}</td>
                      <td>{new Date(req.completedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted" style={{ fontFamily: "'Poppins', sans-serif" }}>No songs were completed.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button className="btn-custom-primary" onClick={() => setShowReport(false)}>Close</Button>
            <Button className="btn-custom-primary" onClick={exportReport}>Export to CSV</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default BandLeader;